<template>
    <IonPage>
        <IonTabs
            class="pb-(--beyond-safe-area-inset-bottom)"
            style="--ion-tab-bar-background: var(--surface-primary);"
            @ion-tabs-did-change="tab = $event"
        >
            <!-- Set mode to "ios" to ensure the view animates in from the side instead of the bottom -->
            <IonRouterOutlet mode="ios" />

            <IonTabBar
                slot="bottom"
                class="border-t-1 border-(--border-neutral-subtle)"
            >
                <IonTabButton
                    tab="home"
                    href="/next-gen/spaces"
                >
                    <DIcon
                        :icon="tabName === 'home' ? 'ph:house-fill' : 'ph:house'"
                        size="w-6 h-6"
                    />
                    <IonLabel class="text-xs mt-1">
                        {{ $t('next_gen.tabs.home.title') }}
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    v-if="version.events_widget_page_url"
                    tab="events"
                    href="/next-gen/events"
                >
                    <DIcon
                        :icon="tabName === 'events' ? 'ph:calendar-blank-fill' : 'ph:calendar-blank'"
                        size="w-6 h-6"
                    />
                    <IonLabel class="text-xs mt-1">
                        {{ $t('next_gen.tabs.events.title') }}
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    v-if="version.news_widget_page_url"
                    tab="news"
                    href="/next-gen/news"
                >
                    <DIcon
                        :icon="tabName === 'news' ? 'ph:newspaper-fill' : 'ph:newspaper'"
                        size="w-6 h-6"
                    />
                    <IonLabel class="text-xs mt-1">
                        {{ $t('next_gen.tabs.news.title') }}
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    v-if="showNotificationsTab"
                    tab="notifications"
                    href="/next-gen/notifications"
                >
                    <div
                        v-if="unreadCount"
                        data-cy="read-indicator"
                        class="p-[3px] min-w-[20px] max-w-[35px] text-[10px] truncate rounded-full bg-(--shape-destructive-base) absolute top-[2px] left-1/2 z-30 text-neutral-on-invert line-clamp-1 leading-none border-2 border-(--surface-primary)"
                    >
                        {{ unreadCount }}
                    </div>
                    <DIcon
                        :icon="tabName === 'notifications' ? 'ph:bell-fill' : 'ph:bell'"
                        size="w-6 h-6"
                    />
                    <IonLabel class="text-xs mt-1">
                        {{ $t('next_gen.tabs.notifications.title') }}
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    tab="more"
                    href="/next-gen/more"
                >
                    <div
                        v-if="showNotificationsBadgeOnMoreTab && unreadCount"
                        data-cy="read-indicator"
                        class="p-[3px] min-w-[20px] max-w-[35px] text-[10px] truncate rounded-full bg-(--shape-destructive-base) absolute top-[2px] left-1/2 z-30 text-neutral-on-invert line-clamp-1 leading-none border-2 border-(--surface-primary)"
                    >
                        {{ unreadCount }}
                    </div>
                    <DIcon
                        v-if="showNotificationsBadgeOnMoreTab"
                        :icon="tabName === 'more' ? 'ph:list-bold' : 'ph:list'"
                        size="w-6 h-6"
                    />
                    <DIcon
                        v-else
                        :icon="tabName === 'more' ? 'ph:circles-four-fill' : 'ph:circles-four'"
                        size="w-6 h-6"
                    />
                    <IonLabel class="text-xs mt-1">
                        {{ $t('next_gen.tabs.more.title') }}
                    </IonLabel>
                </IonTabButton>
            </IonTabBar>
        </IonTabs>
        <DraftVersionBar ref="draftVersionBar" />
    </IonPage>
</template>

<script setup>
    import {
        IonPage,
        IonTabs,
        IonRouterOutlet,
        IonTabBar,
        IonTabButton,
        IonLabel,
        onIonViewWillEnter,
        useIonRouter,
    } from '@ionic/vue'
    import { DIcon } from '@digistorm/spark'
    import { useTourHelpers } from '@/composables/tourHelpers'
    import { events } from '@/composables/events'

    const router = useIonRouter()

    const versionStore = useVersionStore()
    const notificationsStore = useNotificationsStore()
    const moreStore = useMoreStore()
    const pushNotifications = usePushNotifications()
    const tourHelpers = useTourHelpers()

    const tab = ref(null)
    const draftVersionBar = useTemplateRef('draftVersionBar')

    const { version } = storeToRefs(versionStore)
    const { unreadCount } = storeToRefs(notificationsStore)

    const tabName = computed(() => {
        return tab.value?.tab ?? 'home'
    })

    const notificationsEnabled = computed(() => {
        return version.value.notifications_enabled
    })

    const showNotificationsTab = computed(() => {
        return notificationsEnabled.value
            && (version.value.show_notifications_tab ?? true)
    })

    const showNotificationsBadgeOnMoreTab = computed(() => {
        return notificationsEnabled.value
            && !showNotificationsTab.value
    })

    watch(tabName, () => {
        if (tabName.value === 'home') {
            draftVersionBar.value.reset()
        }
    })

    onIonViewWillEnter(async () => {
        // Launch the tour
        if (await tourHelpers.launchTour(router)) {
            // Prevent further execution
            return
        }

        // Remind the user to enable push notifications
        pushNotifications.remindUser()
    })

    const refreshInterface = async (e) => {
        moreStore.load()
        notificationsStore.fetch()
    }

    onMounted(() => {
        notificationsStore.fetch()
        events.on('refresh-interface', refreshInterface)
    })

    onUnmounted(() => {
        events.off('refresh-interface', refreshInterface)
    })
</script>

<style scoped>
    ion-tab-bar {
        min-height: 60px;
        height: auto;
    }

    /**
        The selected colour is meant to be applied via the --color-selected variable, however this variable is also applied when the tab is hovered
        That behaviour can cause issues on android when switching tabs (sometimes pressing on a tab will be treated as a hover)
     */
    ion-tab-button {
        --color: var(--shape-neutral-base);
        --ripple-color: transparent;
        --color-selected: var(--color);
    }

    /**
        Instead apply the selected color via the tab-selected class
     */
    ion-tab-button.tab-selected {
        --color: var(--shape-brand-base);
    }
</style>
